import { useEffect, useState } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Pagination,
  Popconfirm,
  Select,
  InputNumber,
  Tag,
  message,
  Tooltip,
} from "antd";
import { SiMicrosoftexcel } from "react-icons/si";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import moment from "moment";
import * as XLSX from "xlsx";
import PageHeader from "../../component/pageHeaders";
import axios from "../../config/axiosInstance";
import { API } from "../../config/API";

const { RangePicker } = DatePicker;

type PredictionRow = {
  key: number;
  id: number;
  match_id?: string;
  match_label?: string;
  match_date?: string;
  home_team?: string;
  away_team?: string;
  predicted_winner?: string;
  predicted_home_goals?: number;
  predicted_away_goals?: number;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  is_correct?: boolean;
  createdAt?: string;
};

type MatchOption = { match_id: string; match_label: string };

// Compute winner from a score.
const winnerOf = (h: number, a: number): "home" | "away" | "draw" =>
  h > a ? "home" : a > h ? "away" : "draw";

// Did this row predict correctly given an actual result?
const isCorrect = (
  row: PredictionRow,
  actual: { home: number | null; away: number | null }
): boolean | null => {
  if (actual.home === null || actual.away === null) return null;
  const actualWinner = winnerOf(actual.home, actual.away);
  return row.predicted_winner === actualWinner;
};

const buildQuery = (opts: {
  page: number;
  pageSize: number;
  search?: string;
  range?: [string, string] | null;
  matchId?: string;
}) => {
  let f = `?sort=createdAt:desc`;
  f += `&pagination[page]=${opts.page}&pagination[pageSize]=${opts.pageSize}`;
  if (opts.search?.trim()) {
    const s = encodeURIComponent(opts.search.trim());
    f += `&filters[$or][0][user_name][$containsi]=${s}`;
    f += `&filters[$or][1][user_email][$containsi]=${s}`;
    f += `&filters[$or][2][user_phone][$containsi]=${s}`;
    f += `&filters[$or][3][match_label][$containsi]=${s}`;
  }
  if (opts.matchId) {
    f += `&filters[match_id][$eq]=${encodeURIComponent(opts.matchId)}`;
  }
  if (opts.range?.[0] && opts.range?.[1]) {
    f += `&filters[createdAt][$gte]=${opts.range[0]}`;
    f += `&filters[createdAt][$lte]=${opts.range[1]}`;
  }
  return f;
};

const unwrap = (data: any[]): PredictionRow[] =>
  (data || []).map((it: any) => ({
    key: it?.id,
    id: it?.id,
    ...(it?.attributes ?? {}),
  }));

function MatchPredictions() {
  const [rows, setRows] = useState<PredictionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [range, setRange] = useState<[string, string] | null>(null);
  const [selectedMatchId, setSelectedMatchId] = useState<string>("");
  const [matchOptions, setMatchOptions] = useState<MatchOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [marking, setMarking] = useState(false);

  // Actual result entry (shown when a match is selected).
  const [actualHome, setActualHome] = useState<number | null>(null);
  const [actualAway, setActualAway] = useState<number | null>(null);

  // Load match options once (distinct match_id + match_label from all predictions).
  useEffect(() => {
    axios
      .get(
        API.BASE_URL +
          API.MATCH_PREDICTIONS +
          "?fields[0]=match_id&fields[1]=match_label&fields[2]=match_date&pagination[pageSize]=1000&sort=match_date:asc"
      )
      .then((res) => {
        const seen = new Set<string>();
        const opts: MatchOption[] = [];
        for (const it of res.data?.data || []) {
          const mid = it?.attributes?.match_id || it?.match_id;
          const lbl = it?.attributes?.match_label || it?.match_label;
          if (mid && !seen.has(mid)) {
            seen.add(mid);
            opts.push({ match_id: mid, match_label: lbl || mid });
          }
        }
        setMatchOptions(opts);
      })
      .catch(() => {});
  }, []);

  const loadData = (
    p = page,
    ps = pageSize,
    s = search,
    r = range,
    mid = selectedMatchId
  ) => {
    setLoading(true);
    axios
      .get(
        API.BASE_URL +
          API.MATCH_PREDICTIONS +
          buildQuery({ page: p, pageSize: ps, search: s, range: r, matchId: mid })
      )
      .then((res) => {
        setRows(unwrap(res.data?.data || []));
        setTotal(res.data?.meta?.pagination?.total || 0);
      })
      .catch(() => message.error("Failed to load predictions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData(1, pageSize, "", null, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when filters change (debounced for text search).
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      loadData(1, pageSize, search, range, selectedMatchId);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, range, selectedMatchId]);

  // Reset actual result when match changes.
  useEffect(() => {
    setActualHome(null);
    setActualAway(null);
  }, [selectedMatchId]);

  const onPageChange = (p: number, ps: number) => {
    setPage(p);
    setPageSize(ps);
    loadData(p, ps, search, range, selectedMatchId);
  };

  const onDelete = (id: number) => {
    axios
      .delete(API.BASE_URL + API.MATCH_PREDICTION_EDIT + id)
      .then(() => {
        message.success("Deleted");
        loadData();
      })
      .catch(() => message.error("Delete failed"));
  };

  // Bulk-mark is_correct on all predictions for the selected match.
  const markCorrect = async () => {
    if (!selectedMatchId || actualHome === null || actualAway === null) return;
    setMarking(true);
    try {
      // Fetch all rows for this match (no pagination limit for bulk update).
      const res = await axios.get(
        API.BASE_URL +
          API.MATCH_PREDICTIONS +
          buildQuery({ page: 1, pageSize: 1000, matchId: selectedMatchId })
      );
      const all = unwrap(res.data?.data || []);
      const actualWinner = winnerOf(actualHome, actualAway);
      await Promise.all(
        all.map((r) =>
          axios.put(API.BASE_URL + API.MATCH_PREDICTION_EDIT + r.id, {
            data: { is_correct: r.predicted_winner === actualWinner },
          })
        )
      );
      message.success(`Marked ${all.length} predictions`);
      loadData();
    } catch {
      message.error("Failed to mark predictions");
    } finally {
      setMarking(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        API.BASE_URL +
          API.MATCH_PREDICTIONS +
          buildQuery({ page: 1, pageSize: 10000, search, range, matchId: selectedMatchId })
      );
      const all = unwrap(res.data?.data || []);
      const hasActual = actualHome !== null && actualAway !== null;
      const headers = [
        "#", "User", "Email", "Phone", "Match",
        "Predicted Winner", "Pred. Home", "Pred. Away",
        "Match Date", "Submitted On",
        ...(hasActual ? ["Correct?"] : []),
      ];
      const body = all.map((r, i) => {
        const correct = hasActual ? isCorrect(r, { home: actualHome, away: actualAway }) : null;
        return [
          i + 1,
          r.user_name || "",
          r.user_email || "",
          r.user_phone || "",
          r.match_label || `${r.home_team || ""} vs ${r.away_team || ""}`,
          r.predicted_winner === "home"
            ? r.home_team || "Home"
            : r.predicted_winner === "away"
            ? r.away_team || "Away"
            : r.predicted_winner === "draw"
            ? "Draw"
            : r.predicted_winner || "",
          r.predicted_home_goals ?? "",
          r.predicted_away_goals ?? "",
          r.match_date ? moment(r.match_date).format("DD-MM-YYYY HH:mm") : "",
          r.createdAt ? moment(r.createdAt).format("DD-MM-YYYY hh:mm A") : "",
          ...(hasActual ? [correct === true ? "Yes" : correct === false ? "No" : ""] : []),
        ];
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
      XLSX.utils.book_append_sheet(wb, ws, "Predictions");
      XLSX.writeFile(wb, "match-predictions.xlsx");
    } catch {
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const hasActual = actualHome !== null && actualAway !== null;
  const selectedMatch = matchOptions.find((m) => m.match_id === selectedMatchId);

  // Count correct/incorrect when actual result is entered.
  const correctCount = hasActual
    ? rows.filter((r) => isCorrect(r, { home: actualHome, away: actualAway }) === true).length
    : 0;

  const columns = [
    {
      title: "#",
      key: "serial",
      width: 55,
      render: (_: any, __: any, i: number) => (page - 1) * pageSize + i + 1,
    },
    { title: "User", dataIndex: "user_name", key: "user_name", width: 130 },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      render: (v: string) => v || "-",
    },
    { title: "Phone", dataIndex: "user_phone", key: "user_phone", width: 130 },
    {
      title: "Match",
      key: "match",
      render: (_: any, r: PredictionRow) =>
        r.match_label || `${r.home_team || "?"} vs ${r.away_team || "?"}`,
    },
    {
      title: "Predicted Winner",
      dataIndex: "predicted_winner",
      key: "predicted_winner",
      width: 160,
      render: (v: string, r: PredictionRow) => {
        const label =
          v === "home" ? r.home_team || "Home"
          : v === "away" ? r.away_team || "Away"
          : v === "draw" ? "Draw"
          : v || "-";
        return <Tag>{label}</Tag>;
      },
    },
    {
      title: "Score (H–A)",
      key: "score",
      width: 100,
      render: (_: any, r: PredictionRow) =>
        `${r.predicted_home_goals ?? "-"} – ${r.predicted_away_goals ?? "-"}`,
    },
    // Correct? column — only rendered when actual result is entered.
    ...(hasActual
      ? [
          {
            title: "Correct?",
            key: "correct",
            width: 90,
            render: (_: any, r: PredictionRow) => {
              const result = isCorrect(r, { home: actualHome, away: actualAway });
              if (result === true)
                return (
                  <span style={{ color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <IoCheckmarkCircle size={16} /> Yes
                  </span>
                );
              if (result === false)
                return (
                  <span style={{ color: "#dc2626", fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <IoCloseCircle size={16} /> No
                  </span>
                );
              return "-";
            },
          },
        ]
      : []),
    {
      title: "Match Date",
      dataIndex: "match_date",
      key: "match_date",
      width: 140,
      render: (v: string) => (v ? moment(v).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (v: string) => (v ? moment(v).format("DD-MM-YYYY hh:mm A") : "-"),
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_: any, r: PredictionRow) => (
        <Popconfirm
          title="Delete this prediction?"
          onConfirm={() => onDelete(r.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button danger size="small">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Match Predictions"
        length={`${total} Predictions`}
        loader={loading}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* Match filter */}
          <Select
            allowClear
            showSearch
            placeholder="Filter by match"
            style={{ width: 240 }}
            value={selectedMatchId || undefined}
            onChange={(v) => { setSelectedMatchId(v || ""); setPage(1); }}
            optionFilterProp="label"
            options={matchOptions.map((m) => ({
              value: m.match_id,
              label: m.match_label,
            }))}
          />
          {/* Text search */}
          <Input
            allowClear
            placeholder="Search name / email / phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {/* Date range */}
          <RangePicker
            onChange={(_, strings) => {
              setRange(
                strings?.[0] && strings?.[1]
                  ? [`${strings[0]}T00:00:00.000Z`, `${strings[1]}T23:59:59.999Z`]
                  : null
              );
            }}
          />
          <Button icon={<SiMicrosoftexcel />} onClick={exportExcel} loading={exporting}>
            Export
          </Button>
        </div>
      </PageHeader>

      {/* Actual result panel — only shown when a match is selected */}
      {selectedMatchId && (
        <div
          style={{
            margin: "0 16px 12px",
            padding: "14px 18px",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", minWidth: 0 }}>
            {selectedMatch?.match_label || "Selected match"} — Actual result:
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <InputNumber
              min={0}
              max={30}
              placeholder="Home"
              value={actualHome}
              onChange={(v) => setActualHome(v)}
              style={{ width: 70 }}
            />
            <span style={{ fontWeight: 800, color: "#64748b" }}>–</span>
            <InputNumber
              min={0}
              max={30}
              placeholder="Away"
              value={actualAway}
              onChange={(v) => setActualAway(v)}
              style={{ width: 70 }}
            />
          </div>
          {hasActual && (
            <>
              <Tag color="green" style={{ fontSize: 13 }}>
                {correctCount} / {rows.length} correct on this page
              </Tag>
              <Tooltip title="Saves is_correct on every prediction for this match">
                <Button
                  type="primary"
                  size="small"
                  loading={marking}
                  onClick={markCorrect}
                >
                  Save to database
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      )}

      <div style={{ padding: "0 16px 16px" }}>
        <Table
          columns={columns as any}
          dataSource={rows}
          loading={loading}
          pagination={false}
          scroll={{ x: "max-content" }}
          size="middle"
          bordered
          rowClassName={(r) => {
            if (!hasActual) return "";
            const result = isCorrect(r, { home: actualHome, away: actualAway });
            if (result === true) return "row-correct";
            if (result === false) return "row-incorrect";
            return "";
          }}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={["10", "20", "50", "100"]}
            showTotal={(t, r) => `${r[0]}-${r[1]} of ${t}`}
            onChange={onPageChange}
          />
        </div>
      </div>

      {/* Row highlight styles */}
      <style>{`
        .row-correct td { background: #f0fdf4 !important; }
        .row-incorrect td { background: #fff5f5 !important; }
      `}</style>
    </div>
  );
}

export default MatchPredictions;
